import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import {
  MachineReservationFormDialogComponent,
  MachineReservationDialogData,
} from '../machine-reservation-form-dialog/machine-reservation-form-dialog.component';

interface CalendarDay {
  date: Date;
  inMonth: boolean;
  isToday: boolean;
  reservations: any[];
}

@Component({
  selector: 'app-machine-reservation-list',
  templateUrl: './machine-reservation-list.component.html',
  styleUrls: ['./machine-reservation-list.component.css'],
})
export class MachineReservationListComponent implements OnInit {
  viewMode: 'list' | 'calendar' = 'list';
  listLoading = false;
  calendarLoading = false;
  reservations: any[] = [];
  machines: any[] = [];
  calendarDays: CalendarDay[] = [];
  calendarMonth = new Date();

  filters = {
    machine_id: '',
    project_id: '',
    status: '',
    from_date: '',
    to_date: '',
    search: '',
  };

  statusOptions = [
    { value: '', label: 'All statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'in_use', label: 'In use' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  get loading(): boolean {
    return this.viewMode === 'calendar' ? this.calendarLoading : this.listLoading;
  }

  constructor(
    private api: ApiService,
    private toastr: ToastrMsgService,
    private dialog: MatDialog,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['machine_id']) {
        this.filters.machine_id = params['machine_id'];
      }
      if (params['project_id']) {
        this.filters.project_id = params['project_id'];
      }
      this.loadMachines();
      this.refresh();
    });
  }

  private normalizeReservations(res: any): any[] {
    if (Array.isArray(res)) {
      return res;
    }
    if (Array.isArray(res?.data)) {
      return res.data;
    }
    return [];
  }

  refresh(): void {
    if (this.viewMode === 'calendar') {
      this.loadCalendar();
    } else {
      this.load();
    }
  }

  loadMachines(): void {
    this.api.getMachineMaster().subscribe({
      next: (res: any) => {
        this.machines = Array.isArray(res) ? res : res?.data || [];
      },
    });
  }

  load(): void {
    this.listLoading = true;
    const params: any = { limit: 500 };
    if (this.filters.machine_id) params.machine_id = this.filters.machine_id;
    if (this.filters.project_id) params.project_id = this.filters.project_id;
    if (this.filters.status) params.status = this.filters.status;
    if (this.filters.from_date) params.from_date = this.filters.from_date;
    if (this.filters.to_date) params.to_date = this.filters.to_date;
    if (this.filters.search) params.search = this.filters.search;

    this.api.getMachineReservations(params).pipe(
      finalize(() => {
        this.listLoading = false;
      })
    ).subscribe({
      next: (res: any) => {
        this.reservations = this.normalizeReservations(res);
        if (this.viewMode === 'calendar') {
          this.buildCalendar();
        }
      },
      error: (err) => {
        this.reservations = [];
        this.toastr.error(err?.error?.message || 'Failed to load reservations');
      },
    });
  }

  setView(mode: 'list' | 'calendar'): void {
    this.viewMode = mode;
    if (mode === 'calendar') {
      this.loadCalendar();
    }
  }

  loadCalendar(): void {
    this.calendarLoading = true;
    const from = this.calendarRange.from;
    const to = this.calendarRange.to;
    const params: any = { from_date: from, to_date: to };
    if (this.filters.machine_id) params.machine_id = this.filters.machine_id;
    if (this.filters.project_id) params.project_id = this.filters.project_id;

    this.api.getMachineReservationCalendar(params).pipe(
      finalize(() => {
        this.calendarLoading = false;
      })
    ).subscribe({
      next: (res: any) => {
        this.reservations = this.normalizeReservations(res);
        this.buildCalendar();
      },
      error: (err) => {
        this.reservations = [];
        this.calendarDays = [];
        this.toastr.error(err?.error?.message || 'Failed to load calendar');
      },
    });
  }

  get calendarRange(): { from: string; to: string } {
    const year = this.calendarMonth.getFullYear();
    const month = this.calendarMonth.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const start = new Date(first);
    start.setDate(start.getDate() - start.getDay());
    const end = new Date(last);
    end.setDate(end.getDate() + (6 - end.getDay()));
    return {
      from: start.toISOString().slice(0, 10),
      to: end.toISOString().slice(0, 10),
    };
  }

  get calendarTitle(): string {
    return this.calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  prevMonth(): void {
    this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() - 1, 1);
    this.loadCalendar();
  }

  nextMonth(): void {
    this.calendarMonth = new Date(this.calendarMonth.getFullYear(), this.calendarMonth.getMonth() + 1, 1);
    this.loadCalendar();
  }

  buildCalendar(): void {
    try {
      const year = this.calendarMonth.getFullYear();
      const month = this.calendarMonth.getMonth();
      const first = new Date(year, month, 1);
      const last = new Date(year, month + 1, 0);
      const start = new Date(first);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(last);
      end.setDate(end.getDate() + (6 - end.getDay()));

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days: CalendarDay[] = [];

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const date = new Date(d);
        const key = date.toISOString().slice(0, 10);
        const dayReservations = this.reservations.filter((r) => {
          if (!r?.start_date || !r?.end_date) {
            return false;
          }
          const s = String(r.start_date).slice(0, 10);
          const e = String(r.end_date).slice(0, 10);
          return key >= s && key <= e;
        });
        days.push({
          date: new Date(date),
          inMonth: date.getMonth() === month,
          isToday: date.getTime() === today.getTime(),
          reservations: dayReservations,
        });
      }
      this.calendarDays = days;
    } catch (err) {
      console.error('buildCalendar:', err);
      this.calendarDays = [];
    }
  }

  openBookDialog(reservation?: any, machineId?: number): void {
    const dialogRef = this.dialog.open(MachineReservationFormDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: 'first-tabbable',
      panelClass: 'machine-reservation-dialog',
      data: {
        reservation,
        prefillMachineId: machineId,
      } as MachineReservationDialogData,
    });

    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.viewMode === 'calendar' ? this.loadCalendar() : this.load();
      }
    });
  }

  cancelReservation(row: any): void {
    const reason = prompt('Cancellation reason (optional):');
    if (reason === null) return;

    this.api.cancelMachineReservation(row.id, { reason: reason || undefined }).subscribe({
      next: (res: any) => {
        this.toastr.success(res.message || 'Reservation cancelled');
        this.viewMode === 'calendar' ? this.loadCalendar() : this.load();
      },
      error: (err) => this.toastr.error(err?.error?.message || 'Cancel failed'),
    });
  }

  statusClass(status: string): string {
    const map: Record<string, string> = {
      pending: 'bg-gray-100 text-gray-800',
      confirmed: 'bg-blue-100 text-blue-800',
      in_use: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  }

  canCancel(row: any): boolean {
    return !['cancelled', 'completed'].includes(row.status);
  }

  clearFilters(): void {
    this.filters = {
      machine_id: '',
      project_id: '',
      status: '',
      from_date: '',
      to_date: '',
      search: '',
    };
    this.refresh();
  }

  reservationBarClass(status: string): string {
    const map: Record<string, string> = {
      pending: 'bg-gray-400',
      confirmed: 'bg-blue-500',
      in_use: 'bg-purple-500',
      completed: 'bg-green-500',
      cancelled: 'bg-red-400 line-through',
    };
    return map[status] || 'bg-blue-500';
  }
}
